import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { SiteGroup } from '../utils/siteUtils';
import { ServerRecord } from '../data/inventoryData';

interface Props {
  site: SiteGroup | null;
  visibleServers: ServerRecord[];
  onClose: () => void;
}

export default function SiteDrawer({ site, visibleServers, onClose }: Props) {
  if (!site) return null;

  return (
    <Drawer
      anchor="right"
      open={!!site}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 720 }, p: 3 } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            {site.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {visibleServers.length} matching server
            {visibleServers.length !== 1 ? 's' : ''} &middot;{' '}
            {site.Latitude.toFixed(3)}, {site.Longitude.toFixed(3)}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" fontWeight={800} color="error.main">
            {visibleServers.filter((x) =>
              x.serverType.toLowerCase().includes('physical')
            ).length}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Physical
          </Typography>
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" fontWeight={800} color="primary.main">
            {visibleServers.filter(
              (x) => !x.serverType.toLowerCase().includes('physical')
            ).length}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Virtual
          </Typography>
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" fontWeight={800} color="success.main">
            {visibleServers.filter((x) => Number(x.GPUCount) > 0).length}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            GPU
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Server</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 700, minWidth: 220 }}>Resources</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Utilization</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleServers.map((x) => (
              <TableRow key={x.server} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {x.server}
                  </Typography>
                  <Chip
                    label={Number(x.GPUCount) > 0 ? 'GPU' : 'Standard'}
                    size="small"
                    color={Number(x.GPUCount) > 0 ? 'success' : 'default'}
                    sx={{ mt: 0.5, height: 18, fontSize: 10 }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption">{x.serverType}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" component="div" noWrap>
                    CPU: {x.CPUCores} cores
                  </Typography>
                  <Typography variant="caption" component="div" noWrap>
                    Mem: {x.Memory}
                  </Typography>
                  <Typography variant="caption" component="div" noWrap>
                    Disk: {x.DiskSize}
                  </Typography>
                  {x.GPU && x.GPU !== 'NA' && (
                    <Typography variant="caption" component="div" noWrap>
                      GPU: {x.GPU}
                    </Typography>
                  )}
                  {x.GPUMemory && x.GPUMemory !== 'NA' && (
                    <Typography variant="caption" component="div" noWrap>
                      GPU Mem: {x.GPUMemory}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="caption" component="div" noWrap>
                    CPU: {x.CPU_Utilized}
                  </Typography>
                  <Typography variant="caption" component="div" noWrap>
                    Mem: {x.Mem_Utilized}
                  </Typography>
                  <Typography variant="caption" component="div" noWrap>
                    Disk: {x.DiskUtilized}
                  </Typography>
                  {x.GPU_Mem_Utilized !== 'NA' && (
                    <Typography variant="caption" component="div" noWrap>
                      GPU: {x.GPU_Mem_Utilized}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Drawer>
  );
}
